import { useState, useEffect } from 'react';
import { listSgpAuthorizations, listSgpOperations, approveSgpAuthorization, rejectSgpAuthorization } from '../api/sgp';
import { Topbar } from '../layout/Topbar';
import type { SgpAuthorization, SgpAuthorizationStatus, SgpOperationDefinition } from '../types';

const STATUS_MAP: Record<SgpAuthorizationStatus, { label: string; className: string }> = {
  REQUESTED: { label: 'Solicitada', className: 'warning' },
  CUSTOMER_CONFIRMED: { label: 'Cliente confirmou', className: 'primary' },
  APPROVED: { label: 'Aprovada', className: 'success' },
  EXECUTING: { label: 'Executando', className: 'primary' },
  CONSUMED: { label: 'Consumida', className: 'neutral' },
  REJECTED: { label: 'Rejeitada', className: 'danger' },
  EXPIRED: { label: 'Expirada', className: 'neutral' },
  FAILED: { label: 'Falha', className: 'danger' },
  CANCELED: { label: 'Cancelada', className: 'neutral' },
};

function formatTime(date: string): string {
  const d = new Date(date);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function SgpAuthorizationsPage() {
  const [authorizations, setAuthorizations] = useState<SgpAuthorization[]>([]);
  const [operations, setOperations] = useState<SgpOperationDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuth, setSelectedAuth] = useState<SgpAuthorization | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('PENDING');
  const [approvalReason, setApprovalReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [auths, ops] = await Promise.all([
        listSgpAuthorizations(),
        listSgpOperations(),
      ]);
      setAuthorizations(auths as SgpAuthorization[]);
      setOperations(ops as SgpOperationDefinition[]);
    } catch (err) { console.error('Falha ao carregar autorizações:', err); } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getOperationName = (opId: string) => {
    return operations.find((o) => o.id === opId)?.name || opId;
  };

  const filtered = authorizations.filter((a) => {
    if (filterStatus === 'PENDING') return a.status === 'REQUESTED' || a.status === 'CUSTOMER_CONFIRMED';
    if (filterStatus === 'ALL') return true;
    return a.status === filterStatus;
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const handleApprove = async () => {
    if (!selectedAuth) return;
    setActionLoading(true);
    setActionMsg('');
    try {
      await approveSgpAuthorization(selectedAuth.id, approvalReason || undefined);
      setActionMsg('✅ Autorização aprovada com sucesso!');
      setSelectedAuth(null);
      setApprovalReason('');
      loadData();
    } catch (err: any) {
      setActionMsg(`❌ Erro: ${err.message || 'Falha ao aprovar'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAuth || !rejectionReason.trim()) return;
    setActionLoading(true);
    setActionMsg('');
    try {
      await rejectSgpAuthorization(selectedAuth.id, rejectionReason);
      setActionMsg('✅ Autorização rejeitada.');
      setSelectedAuth(null);
      setRejectionReason('');
      loadData();
    } catch (err: any) {
      setActionMsg(`❌ Erro: ${err.message || 'Falha ao rejeitar'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const hasPending = authorizations.some((a) => a.status === 'REQUESTED' || a.status === 'CUSTOMER_CONFIRMED');

  return (
    <div className="view-container">
      <Topbar title="Autorizações SGP" subtitle="Aprovação e rejeição de operações críticas e sensíveis." />

      {hasPending && (
        <div className="alert-banner warning">
          ⚠️ Há autorizações pendentes aguardando análise.
        </div>
      )}

      <div className="flex gap-sm" style={{ marginBottom: 16, alignItems: 'center' }}>
        <span className="text-sm text-secondary">Filtrar:</span>
        {(['PENDING', 'ALL', 'APPROVED', 'REJECTED', 'CONSUMED', 'FAILED', 'EXPIRED'] as const).map((s) => (
          <button
            key={s}
            className={`queue-tab${filterStatus === s ? ' active' : ''}`}
            onClick={() => setFilterStatus(s)}
            type="button"
          >
            {{ PENDING: '📋 Pendentes', ALL: '📋 Todas', APPROVED: '✅ Aprovadas', REJECTED: '❌ Rejeitadas', CONSUMED: '⚡ Consumidas', FAILED: '💥 Falhas', EXPIRED: '⏰ Expiradas' }[s]}
            <span className="queue-count">
              {s === 'ALL' ? authorizations.length : authorizations.filter((a) => {
                if (s === 'PENDING') return a.status === 'REQUESTED' || a.status === 'CUSTOMER_CONFIRMED';
                return a.status === s;
              }).length}
            </span>
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button className="secondary" onClick={loadData} disabled={loading}>
          {loading ? '🔄' : '🔄 Atualizar'}
        </button>
      </div>

      <div className="records-layout">
        {/* List */}
        <div className="panel">
          <div className="panel-header">
            <h2>Autorizações</h2>
            <span className="text-sm text-secondary">{filtered.length} registros</span>
          </div>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state"><p>Nenhuma autorização encontrada.</p></div>
          ) : (
            <div className="data-list">
              {filtered.map((auth) => {
                const st = STATUS_MAP[auth.status] || { label: auth.status, className: 'neutral' };
                return (
                  <div
                    key={auth.id}
                    className={`data-list-item${selectedAuth?.id === auth.id ? ' active' : ''}`}
                    onClick={() => { setSelectedAuth(auth); setActionMsg(''); }}
                  >
                    <div>
                      <div className="item-name">{getOperationName(auth.operationId)}</div>
                      <div className="item-detail">
                        {auth.requestedByUser?.name || 'Agente'} • {formatTime(auth.createdAt)}
                        {auth.payloadSummary?.contractId ? ` • Contrato: ${auth.payloadSummary.contractId}` : ''}
                      </div>
                    </div>
                    <span className={`pill ${st.className}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="panel">
          {!selectedAuth ? (
            <div className="empty-state" style={{ minHeight: 300 }}>
              <span className="icon">🔐</span>
              <p>Selecione uma autorização para analisar.</p>
            </div>
          ) : (
            <>
              <div className="panel-header">
                <h2>Detalhe da autorização</h2>
                <span className={`pill ${STATUS_MAP[selectedAuth.status]?.className || 'neutral'}`}>
                  {STATUS_MAP[selectedAuth.status]?.label || selectedAuth.status}
                </span>
              </div>
              <div className="panel-body">
                <div className="customer-details">
                  <div className="detail-row">
                    <span className="label">Operação</span>
                    <span className="value"><strong>{getOperationName(selectedAuth.operationId)}</strong></span>
                  </div>
                  <div className="detail-row">
                    <span className="label">ID Operação</span>
                    <span className="value mono">{selectedAuth.operationId}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Risco</span>
                    <span className="value"><span className={`pill ${selectedAuth.risk === 'CRITICAL' ? 'danger' : 'warning'}`}>{selectedAuth.risk}</span></span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Solicitante</span>
                    <span className="value">{selectedAuth.requestedByUser?.name || 'Sistema'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Criada em</span>
                    <span className="value">{formatTime(selectedAuth.createdAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Expira em</span>
                    <span className="value">{formatTime(selectedAuth.expiresAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Motivo</span>
                    <span className="value">{selectedAuth.requestReason}</span>
                  </div>

                  {selectedAuth.payloadSummary && Object.keys(selectedAuth.payloadSummary).length > 0 && (
                    <div className="detail-section" style={{ marginTop: 8 }}>
                      <span className="detail-section-title">Payload resumido</span>
                      <pre className="payload-preview">
                        {JSON.stringify(selectedAuth.payloadSummary, null, 2)}
                      </pre>
                    </div>
                  )}

                  {selectedAuth.approvedByUser && (
                    <div className="detail-row">
                      <span className="label">Aprovado por</span>
                      <span className="value">{selectedAuth.approvedByUser.name} {selectedAuth.approvalReason ? `• ${selectedAuth.approvalReason}` : ''}</span>
                    </div>
                  )}

                  {selectedAuth.rejectionReason && (
                    <div className="detail-row">
                      <span className="label">Motivo rejeição</span>
                      <span className="value" style={{ color: 'var(--color-danger)' }}>{selectedAuth.rejectionReason}</span>
                    </div>
                  )}
                </div>

                {/* Action: Approve / Reject */}
                {(selectedAuth.status === 'REQUESTED' || selectedAuth.status === 'CUSTOMER_CONFIRMED') && (
                  <div className="action-stack">
                    {actionMsg && <p className="text-sm" style={{ color: actionMsg.includes('✅') ? 'var(--color-success)' : 'var(--color-danger)' }}>{actionMsg}</p>}

                    <div className="operation-box">
                      <span className="operation-title">✅ Aprovar</span>
                      <input value={approvalReason} onChange={(e) => setApprovalReason(e.target.value)} placeholder="Justificativa (opcional)" />
                      <button onClick={handleApprove} disabled={actionLoading}>
                        {actionLoading ? 'Processando...' : 'Aprovar autorização'}
                      </button>
                    </div>

                    <div className="operation-box">
                      <span className="operation-title">❌ Rejeitar</span>
                      <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Motivo da rejeição (obrigatório)" rows={2} />
                      <button className="danger" onClick={handleReject} disabled={actionLoading || !rejectionReason.trim()}>
                        {actionLoading ? 'Processando...' : 'Rejeitar autorização'}
                      </button>
                    </div>
                  </div>
                )}

                {selectedAuth.status === 'CONSUMED' && (
                  <div className="action-stack">
                    <span className="text-sm text-secondary text-center">✅ Autorização consumida — operação executada com sucesso.</span>
                  </div>
                )}

                {selectedAuth.status === 'FAILED' && (
                  <div className="action-stack">
                    <span className="text-sm" style={{ color: 'var(--color-danger)' }}>
                      ❌ Falha na execução. {selectedAuth.failureReason ? `Motivo: ${selectedAuth.failureReason}` : ''}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
